(() => {
  'use strict';

  var FREE_DELIVERY_MIN_QTY = 1;
  var SINGLE_DELIVERY_CENTS = 0;
  var TEASER_DELAY_MS = 20000;
  var SUPPORTED_CURRENCY = 'ZAR';
  var SUPPRESSION_KEY = 'fullykitted:first20:dismissed';
  var DISCOUNT_HANDOFF_KEY = 'fullykitted:welcome15:handoff';
  var DISCOUNT_HANDOFF_TTL_MS = 10 * 60 * 1000;

  function toQty(value) {
    var n = Math.floor(Number(value));
    return Number.isFinite(n) ? n : 0;
  }

  function deliveryCents(quantity) {
    var qty = toQty(quantity);
    if (qty < 1) return null;
    return qty >= FREE_DELIVERY_MIN_QTY ? 0 : SINGLE_DELIVERY_CENTS;
  }

  function estimateFor(unitPriceCents, quantity, currencyIso) {
    if (currencyIso !== SUPPORTED_CURRENCY) return null;
    var unit = Math.round(Number(unitPriceCents));
    var qty = toQty(quantity);
    if (!Number.isFinite(unit) || unit < 0 || qty < 1) return null;
    var shipping = deliveryCents(qty);
    if (shipping === null) return null;
    return { quantity: qty, shippingCents: shipping, totalCents: unit * qty + shipping, freeDelivery: shipping === 0 };
  }

  function clampQuantity(raw, current, max) {
    var n = Number(raw);
    var fallback = toQty(current) >= 1 ? toQty(current) : 1;
    if (!Number.isInteger(n) || n < 1) return { valid: false, quantity: fallback };
    if (max !== undefined && max !== null && max !== '' && n > toQty(max)) {
      return { valid: false, quantity: fallback };
    }
    return { valid: true, quantity: n };
  }

  function normalizeVariant(option) {
    if (!option) return null;
    var price = Math.round(Number(option.price));
    if (!Number.isFinite(price) || price < 0) return null;
    var max = option.max === undefined || option.max === null || option.max === '' ? null : toQty(option.max);
    return { priceCents: price, available: option.available === true, maxQty: max && max >= 1 ? max : null };
  }

  function teaserDue(state) {
    if (!state || state.dismissed || state.overlayOpen || state.buyAreaVisible || !state.engaged) return false;
    return Number(state.elapsedMs) >= TEASER_DELAY_MS;
  }

  function readSuppression(storage) {
    try {
      if (!storage || typeof storage.getItem !== 'function') return false;
      return storage.getItem(SUPPRESSION_KEY) === '1';
    } catch (e) {
      return false;
    }
  }

  function writeSuppression(storage) {
    try {
      if (!storage || typeof storage.setItem !== 'function') return false;
      storage.setItem(SUPPRESSION_KEY, '1');
      return true;
    } catch (e) {
      return false;
    }
  }

  function writeDiscountHandoff(storage, handoff, now) {
    try {
      if (!storage || typeof storage.setItem !== 'function' || !handoff) return false;
      var path = String(handoff.path || '');
      var variantId = String(handoff.variantId || '');
      var quantity = Number(handoff.quantity);
      if (!path.startsWith('/') || path.startsWith('//') || path.includes('?') || path.includes('#')) return false;
      if (!/^\d+$/.test(variantId) || !Number.isSafeInteger(quantity) || quantity < 1) return false;
      var timestamp = Number.isFinite(Number(now)) ? Number(now) : Date.now();
      storage.setItem(DISCOUNT_HANDOFF_KEY, JSON.stringify({
        path: path,
        variantId: variantId,
        quantity: quantity,
        expiresAt: timestamp + DISCOUNT_HANDOFF_TTL_MS,
      }));
      return true;
    } catch (e) {
      return false;
    }
  }

  function consumeDiscountHandoff(storage, currentPath, now) {
    try {
      if (!storage || typeof storage.getItem !== 'function') return null;
      var raw = storage.getItem(DISCOUNT_HANDOFF_KEY);
      if (!raw) return null;
      var handoff = JSON.parse(raw);
      var timestamp = Number.isFinite(Number(now)) ? Number(now) : Date.now();
      if (!handoff || !Number.isFinite(handoff.expiresAt) || handoff.expiresAt <= timestamp) {
        storage.removeItem(DISCOUNT_HANDOFF_KEY);
        return null;
      }
      if (handoff.path !== currentPath) return null;
      storage.removeItem(DISCOUNT_HANDOFF_KEY);
      if (!/^\d+$/.test(String(handoff.variantId || '')) || !Number.isSafeInteger(handoff.quantity) || handoff.quantity < 1) return null;
      return { path: handoff.path, variantId: String(handoff.variantId), quantity: handoff.quantity };
    } catch (e) {
      try { storage?.removeItem?.(DISCOUNT_HANDOFF_KEY); } catch (ignored) { /* storage may be unavailable */ }
      return null;
    }
  }

  function inlineOfferDue(physicalCount) {
    return toQty(physicalCount) >= 2;
  }

  function restoreTarget(origin) {
    if (origin && origin.connected && !origin.isBody && !origin.insideDialog && origin.visible !== false) return 'origin';
    if (origin && origin.stickyVisible && origin.stickyEnabled) return 'sticky';
    if (origin && origin.addVisible && origin.addEnabled) return 'add';
    return 'main';
  }

  function formatZar(cents) {
    var n = Math.round(Number(cents));
    if (!Number.isFinite(n) || n < 0) return null;
    var parts = (n / 100).toFixed(2).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return 'R' + parts.join('.');
  }

  function createSubmitGuard() {
    var inFlight = false;
    return {
      tryAcquire: function () {
        if (inFlight) return false;
        inFlight = true;
        return true;
      },
      release: function () {
        inFlight = false;
      },
    };
  }

  var api = {
    FREE_DELIVERY_MIN_QTY: FREE_DELIVERY_MIN_QTY,
    SINGLE_DELIVERY_CENTS: SINGLE_DELIVERY_CENTS,
    TEASER_DELAY_MS: TEASER_DELAY_MS,
    SUPPORTED_CURRENCY: SUPPORTED_CURRENCY,
    SUPPRESSION_KEY: SUPPRESSION_KEY,
    deliveryCents: deliveryCents,
    estimateFor: estimateFor,
    clampQuantity: clampQuantity,
    normalizeVariant: normalizeVariant,
    teaserDue: teaserDue,
    readSuppression: readSuppression,
    writeSuppression: writeSuppression,
    DISCOUNT_HANDOFF_KEY: DISCOUNT_HANDOFF_KEY,
    DISCOUNT_HANDOFF_TTL_MS: DISCOUNT_HANDOFF_TTL_MS,
    writeDiscountHandoff: writeDiscountHandoff,
    consumeDiscountHandoff: consumeDiscountHandoff,
    inlineOfferDue: inlineOfferDue,
    restoreTarget: restoreTarget,
    formatZar: formatZar,
    createSubmitGuard: createSubmitGuard,
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  } else if (typeof window !== 'undefined') {
    window.FullyKitted = window.FullyKitted || {};
    window.FullyKitted.flow = api;
  }
})();

